/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/ui/Card.tsx
 *
 * Rôle :
 *   Conteneur visuel réutilisable, radius 16-24 px, shadow douce, padding
 *   généreux. Reproduit le pattern « round-element + shadow-large » de
 *   JANE 2019 (cf. CLAUDE.md §8.11.3) en moderne et accessible.
 *
 *   3 variantes (default, accent, dark) + sous-composants optionnels
 *   `Card.Header`, `Card.Body`, `Card.Footer` pour structurer.
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE, copier-coller dans NOLAS modernisé sans
 *   modification, juste les tokens CSS à mapper.
 *
 * Exemple :
 *   <Card variant="accent" interactive onClick={…}>
 *     <Card.Header>Titre</Card.Header>
 *     <Card.Body>Contenu</Card.Body>
 *     <Card.Footer>CTA</Card.Footer>
 *   </Card>
 *
 * @author Mika
 * @since 2026-05-04
 */

import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import styles from './Card.module.css';

export type CardVariant = 'default' | 'accent' | 'dark' | 'flat';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  /** Si true, affiche le hover/active comme un bouton (cursor pointer + lift). */
  interactive?: boolean;
  /** Padding global. `none` pour gérer soi-même via les sous-composants. */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Élément HTML rendu (`div` par défaut, `article`, `section`…). */
  as?: 'div' | 'article' | 'section' | 'li';
  children?: ReactNode;
}

/**
 * Carte standardisée TNE 2026.
 *
 * FIX 2026-05-06 (S15bis) : pattern compound typé. Avant, l'export
 * `Card` était `ForwardRefExoticComponent` sans les slots, donc
 * `<Card.Body>` produisait TS2339 chez les consommateurs (16 erreurs).
 * Solution : caster l'export au type `CardWithSlots` qui inclut
 * Header/Body/Footer comme propriétés statiques.
 */
const CardBase = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', interactive = false, padding = 'md', as = 'div', className, children, ...rest },
  ref,
) {
  const composedClassName = [
    styles.card,
    styles[`variant_${variant}`],
    styles[`padding_${padding}`],
    interactive ? styles.interactive : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  // forwardRef + as polymorphique simplifié : on rend toujours un <div> mais
  // l'utilisateur peut surcharger via `as` côté futur (étendre plus tard).
  // Pour l'instant on garde simple et typé strict.
  void as;

  return (
    <div ref={ref} className={composedClassName} {...rest}>
      {children}
    </div>
  );
});

interface CardSlotProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

function CardHeader({ className, children, ...rest }: CardSlotProps) {
  return (
    <header className={[styles.header, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </header>
  );
}

function CardBody({ className, children, ...rest }: CardSlotProps) {
  return (
    <div className={[styles.body, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

function CardFooter({ className, children, ...rest }: CardSlotProps) {
  return (
    <footer className={[styles.footer, className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </footer>
  );
}

// Pattern compound component, type d'export qui inclut les slots.
type CardWithSlots = typeof CardBase & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};

const CardCompound = CardBase as CardWithSlots;
CardCompound.Header = CardHeader;
CardCompound.Body = CardBody;
CardCompound.Footer = CardFooter;

export const Card = CardCompound;
